<template>
  <div v-if="enabledSwipes.length" class="gs-section">
    <div class="gs-section__toolbar">
      <section-search v-model="query" placeholder="Search swipes" />
      <p class="gs-section__count">{{ countLabel }}</p>
    </div>

    <div v-if="visibleSwipes.length" class="gs-asset-grid gs-asset-grid--wide">
      <asset-card
        v-for="swipe in visibleSwipes"
        :key="previewKey(swipe)"
        collapsible
        :title="swipe.name"
        :subtitle="subjectsLabel(swipe)"
        :badges="subjectBadges(swipe)"
      >
        <template slot="toolbar">
          <div class="gs-segmented" role="group" aria-label="Body view">
            <button
              v-for="view in views"
              :key="view.value"
              type="button"
              class="gs-segmented__item"
              :class="{ 'is-current': viewFor(swipe) === view.value }"
              :aria-pressed="viewFor(swipe) === view.value ? 'true' : 'false'"
              :data-view="view.value"
              @click="setView(swipe, view.value)"
            >
              {{ view.title }}
            </button>
          </div>
        </template>

        <template slot="preview">
          <!-- The vendor's own HTML, already personalised server side by
               `fetch-funnel-resource`; rendering it is the same trust level as
               the read-only editor it replaces. -->
          <div
            v-if="viewFor(swipe) === 'preview'"
            class="gs-asset-html"
            :class="'gs-preview-' + previewKey(swipe)"
            v-html="previewHtml[previewKey(swipe)]"
          ></div>
          <pre v-else-if="viewFor(swipe) === 'html'" class="gs-asset-code">{{ swipe.htmlBody }}</pre>
          <pre v-else class="gs-asset-text">{{ swipe.textBody }}</pre>
        </template>

        <template slot="actions">
          <!-- Both flavours carry the markup: a plain-text-only target (a
               forum box, a terminal) received the HTML from the legacy copy
               button too, and pasting the plain-text body there instead would
               silently drop every link and image. -->
          <copy-button
            :text="swipe.htmlBody"
            :html="swipe.htmlBody"
            label="Copy HTML"
            :title="$t('frontend_resource_copy_swipe')"
          />
          <copy-button
            :text="swipe.textBody"
            label="Copy text"
            :title="$t('frontend_resource_copy_swipe')"
          />
          <copy-button
            v-if="firstSubject(swipe)"
            :text="firstSubject(swipe)"
            label="Copy subject"
            title="Copy the first subject line"
          />
        </template>
      </asset-card>
    </div>

    <div v-else class="gs-empty">
      <span class="gs-empty__icon"><gs-icon name="search" size="22" /></span>
      <p class="gs-empty__title">No swipes match “{{ query }}”</p>
      <p class="gs-empty__text">Search looks at the swipe name and its subject lines.</p>
    </div>
  </div>

  <div v-else class="gs-empty">
    <span class="gs-empty__icon"><gs-icon name="edit" size="22" /></span>
    <p class="gs-empty__title">{{ $t('frontend_resource_no_swipes') }}</p>
    <p class="gs-empty__text">Email swipes the vendor writes for you show up here.</p>
  </div>
</template>

<script>
import AssetCard from '@/components/promotools/AssetCard.vue';
import CopyButton from '@/components/promotools/CopyButton.vue';
import { previewKeyMap } from '@/components/promotools/previewKeys';
import { sanitiseHtml } from '@/components/promotools/sanitiseHtml';
import SectionSearch from '@/components/promotools/SectionSearch.vue';

export default {
  components: {
    AssetCard,
    CopyButton,
    SectionSearch,
  },
  props: {
    funnelResource: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      query: '',
      // Per-swipe body view, keyed by preview key: rendered, source or text.
      // Null-prototype, so an asset whose key is `__proto__` cannot reach
      // Object.prototype instead of its own entry.
      swipeViews: Object.create(null),
    };
  },
  computed: {
    // "View HTML" and "Text View" are the vendor-side labels these bodies have
    // always carried; only "Preview" is new, so only it is English.
    views() {
      return [
        { title: 'Preview', value: 'preview' },
        { title: this.$t('frontend_resources_view_html'), value: 'html' },
        { title: this.$t('frontend_resource_text_view'), value: 'text' },
      ];
    },
    enabledSwipes() {
      return (this.funnelResource && this.funnelResource.swipes
        ? this.funnelResource.swipes.filter(swipe => swipe.enabled)
        : []);
    },
    visibleSwipes() {
      const query = this.query.trim().toLowerCase();

      if (!query) {
        return this.enabledSwipes;
      }

      return this.enabledSwipes.filter(swipe => {
        const haystack = [swipe.name].concat(this.subjectsOf(swipe)).join(' ').toLowerCase();
        return haystack.indexOf(query) !== -1;
      });
    },
    countLabel() {
      const shown = this.visibleSwipes.length;
      const total = this.enabledSwipes.length;
      return shown === total
        ? `${total} ${total === 1 ? 'swipe' : 'swipes'}`
        : `${shown} of ${total} swipes`;
    },
    previewKeys() {
      return previewKeyMap(this.enabledSwipes);
    },
    // Vendor markup is previewed, never executed. Sanitising is a parse, so
    // it happens once per asset here rather than on every re-render; the map
    // is rebuilt whenever the funnel resource changes. The swipe's own
    // stylesheet is rewritten to match only inside this card's preview well.
    //
    // Only the swipes on screen are parsed. `previewKeys` still covers every
    // enabled swipe, so a card keeps the same key (and the same view choice)
    // while a search narrows the list around it.
    previewHtml() {
      return this.visibleSwipes.reduce((map, swipe) => {
        const key = this.previewKeys.get(swipe);
        map[key] = sanitiseHtml(swipe.htmlBody, { scope: `gs-preview-${key}` });
        return map;
      }, Object.create(null));
    },
  },
  methods: {
    subjectsOf(swipe) {
      return (swipe.subjects || []).filter(subject => subject);
    },
    firstSubject(swipe) {
      return this.subjectsOf(swipe)[0] || '';
    },
    subjectsLabel(swipe) {
      const count = this.subjectsOf(swipe).length;

      if (!count) {
        return '';
      }

      return `${count} ${count === 1 ? 'subject line' : 'subject lines'}`;
    },
    subjectBadges(swipe) {
      return this.subjectsOf(swipe).map(subject => ({
        label: subject,
        copyText: subject,
        copyTitle: 'Copy this subject line',
      }));
    },
    previewKey(swipe) {
      return this.previewKeys.get(swipe) || '';
    },
    viewFor(swipe) {
      return this.swipeViews[this.previewKey(swipe)] || 'preview';
    },
    setView(swipe, view) {
      this.$set(this.swipeViews, this.previewKey(swipe), view);
    },
  },
};
</script>
